import { CustomIcon } from "components/icon/customIcon.component";
import { ColorV2 } from "config/colors";
import { TypographyV2 } from "config/font";
import styled, { css } from "styled-components";

/** Option definition for segment controller items. */
export interface SegmentOption {
  value: string;
  label: string;
  iconName?: string;
}

/** Props for the segment controller component. */
export interface SegmentControllerProps {
  options: SegmentOption[];
  value: string;
  onChange: (value: string) => void;
  label?: string;
  disabled?: boolean;
}

const HEIGHT = 56;
const CONTAINER_PADDING_Y = 8;
const CONTAINER_PADDING_X = 16;
const CONTAINER_GAP = 16;

/** Horizontal segment controller with optional label and icons. */
export const SegmentController = ({
  options,
  value,
  onChange,
  label,
  disabled = false,
}: SegmentControllerProps): JSX.Element => {
  const handleSelect = (selectedValue: string): void => {
    if (disabled) return;
    onChange(selectedValue);
  };

  return (
    <Wrapper>
      {label && <Description>{label}</Description>}
      <Container role="tablist" aria-disabled={disabled}>
        {options.map((option) => {
          const isSelected = option.value === value;
          return (
            <Segment
              key={option.value}
              type="button"
              role="tab"
              aria-selected={isSelected}
              $isSelected={isSelected}
              onClick={() => handleSelect(option.value)}
              disabled={disabled}
            >
              {option.iconName && (
                <CustomIcon
                  name={option.iconName}
                  size="20px"
                  color={ColorV2.Blue1}
                />
              )}
              <Label>{option.label}</Label>
            </Segment>
          );
        })}
      </Container>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 16px;
`;

const Description = styled.span`
  ${TypographyV2.b1}
  color: ${ColorV2.Blue1};
`;

const Container = styled.div`
  display: inline-flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: ${CONTAINER_GAP}px;
  padding: ${CONTAINER_PADDING_Y}px ${CONTAINER_PADDING_X}px;
  height: ${HEIGHT}px;
  border-radius: 16px;
  background-color: ${ColorV2.White};
`;

const Segment = styled.button<{ $isSelected: boolean }>`
  ${TypographyV2.b1}
  display: inline-flex;
  align-items: center;
  gap: 12px;
  border: none;
  border-radius: 12px;
  padding: 8px 16px;
  background-color: transparent;
  color: ${ColorV2.Blue1};
  cursor: pointer;
  transition: background-color 0.15s ease;
  white-space: nowrap;

  &:hover {
    background-color: ${ColorV2.Blue7};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  &:disabled:hover {
    background-color: transparent;
  }

  ${(props) =>
    props.$isSelected &&
    css`
      background-color: ${ColorV2.Blue6};

      &:hover {
        background-color: ${ColorV2.Blue6};
      }
    `}
`;

const Label = styled.span`
  ${TypographyV2.b1}
  color: inherit;
`;
